import type {GetOffersPayload} from '@yandex-delivery/api/service/offer'
import {
  applySpec,
  defaultTo,
  flow,
  get,
  ifElse,
  invert,
  isNotUndefined,
  makeGet,
  pipe,
  toNumber,
  toUndefined,
} from '@yandex-delivery/utils'

import {appConfig} from 'src/shared/config'
import type {Order} from 'src/shared/types'
import {DeliveryType} from 'src/shared/types'
import {toGeoLocation} from 'src/shared/utils'
import {toCargoItemSizes, toDefaultWeight} from 'src/shared/utils/from-sdd-express-product'

// Types
type RoutePoints = GetOffersPayload['routePoints']
type PaymentInfo = GetOffersPayload['paymentInfo']

// Getters
const orderGetter = makeGet<Order>()
const getPaymentInfo = orderGetter('paymentInfo')

// Default Values
const toDefaultPaymentMethod: (value?: string | null) => string = defaultTo('card')
const toDefaultPaymentType: (value?: string | null) => string = defaultTo('cargocorp')

// Mappers
const toPaymentInfo: (order: Order) => PaymentInfo = applySpec<PaymentInfo>({
  isDisabled: flow(getPaymentInfo, get('hasDisableReason')),
  method: flow(getPaymentInfo, get('kind'), toDefaultPaymentMethod),
  type: flow(getPaymentInfo, get('type'), toDefaultPaymentType),
  methodId: flow(getPaymentInfo, get('methodId')),
})

function buildRoutePoints(order: Order): RoutePoints {
  return [order.sourcePoint, ...order.destinationPoints]
    .map(point => point.address?.coordinates)
    .map(coordinates => toGeoLocation(coordinates))
    .filter(isNotUndefined)
}

function buildTariffRequirements(order: Order): GetOffersPayload['tariffRequirements'] {
  const verticalName = order.offerFlow?.vertical?.name
  const verticalRequirements = order.offerFlow?.vertical?.requirements
  const deliveryTime = order.offerFlow?.deliveryInterval?.from

  const result: GetOffersPayload['tariffRequirements'] = {
    taxi_class: [],
  }

  if (deliveryTime) {
    result.due = deliveryTime
  }

  if (verticalName) {
    result.taxi_class = [verticalName]
  }

  if (verticalRequirements) {
    Object.entries(verticalRequirements).forEach(([requirementName, requirement]) => {
      if (Array.isArray(requirement.selectedValue)) {
        result[requirementName] = requirement.selectedValue.length > 0 ? requirement.selectedValue : undefined
      } else if (requirement.name === 'cargo_loaders') {
        result[requirementName] = toNumber(requirement.selectedValue)
      } else {
        result[requirementName] = requirement.selectedValue
      }
    })
  }

  result.skip_door_to_door = pipe(result.door_to_door, ifElse(isNotUndefined, invert, toUndefined))
  /**
   * это свойство надо выставлять в undefined,
   * так как на сервере ожидается skip_door_to_door, а с бэкенда приходит door_to_door
   */
  result.door_to_door = undefined

  return result
}

function buildDeliveryInterval(order: Order): GetOffersPayload['deliveryInterval'] {
  const verticalName = order.offerFlow?.vertical?.name
  const deliveryInterval = order.offerFlow?.deliveryInterval

  return verticalName === DeliveryType.Night ? deliveryInterval : undefined
}

function buildItems(order: Order): GetOffersPayload['items'] {
  return order.destinationPoints?.flatMap((destinationPoint, index) => {
    return destinationPoint.parcels
      ?.flatMap(parcel => parcel?.products)
      .map(product => ({
        destinationPointId: index + 1,
        sourcePointId: 0,
        title: product.name || '',
        quantity: toNumber(product.count),
        currency: appConfig.currency,
        size: toCargoItemSizes(product),
        weight: toDefaultWeight(product.weight),
        cost: product.price,
      }))
  })
}

function getPreOrderId(order: Order): string | undefined {
  return pipe(order, get('_preOrderId'))
}

function getReferralSource(order: Order): string | undefined {
  return pipe(order, get('_referralSource'))
}

export function toGetOffersPayload(order: Order): GetOffersPayload {
  const result: GetOffersPayload = {
    routePoints: buildRoutePoints(order),
    paymentInfo: toPaymentInfo(order),
    tariffRequirements: buildTariffRequirements(order),
    deliveryInterval: buildDeliveryInterval(order),
    items: buildItems(order),
    preOrderId: getPreOrderId(order),
    referralSource: getReferralSource(order),
  }

  if (order.offerFlow.deliveryType === 'toTime') {
    result.due = order.offerFlow.deliveryInterval?.from
  }

  return result
}
